import { X_IDEMPOTENT_HEADER, X_TRACE_ID_HEADER } from '../config';
import { v4 as uuidv4 } from 'uuid';
import Utils from './utils';
import deepmerge from 'deepmerge';

class RequestResponseHelpers {
  static generateClientSideCsrf(config) {
    let token = '';
    const cookieName = 'CSRF-TOKEN';
    const headerName = 'X-CSRF-TOKEN';
    config.headers = config.headers || {};
    if (Utils.isServerSide(config)) {
      const xCookieDict = config.headers['x-cookie-dict'] || {};
      token = xCookieDict[cookieName] || uuidv4();
    } else {
      token = Utils.getCookie(cookieName) || uuidv4();
      Utils.setCookie(cookieName, token);
    }
    config.headers[headerName] = token;
  }

  static hideSensitiveData(data) {
    const sensitiveData = [
      'password',
      'password_repeat',
      'password_confirm',
      'newPassword',
      'oldPassword',
      'old_password',
      'new_password',
      'siteKey',
      'apiKey',
      'validators',
      'validator',
      'current_validators',
      'new_validators',
    ];
    const d = Utils.parseToJSON(data);
    const newData = deepmerge({}, d || {});
    Object.keys(newData).forEach((key) => {
      if (sensitiveData.includes(key)) {
        const value = sensitiveData[key] || '';
        newData[key] = '*'.repeat(value.length);
      }
    });
    return newData;
  }

  static whiteListedHeaders(headers) {
    const whiteListItems = [
      'blaize_session',
      'JSESSIONID',
      'X-CSRF-TOKEN',
      X_TRACE_ID_HEADER,
      'x-correlation-id',
      'x-generated-at',
      'x-zephr-request-id',
      'x-blaize-request',
      'x-cache',
      'x-zephr-cache',
      'Content-Type',
      'authRefreshed',
    ].map(item => item.toLowerCase());
    const res = {};
    Object.keys(headers).forEach((key) => {
      const lowerCaseKey = key.toLowerCase();
      if (whiteListItems.includes(lowerCaseKey)) {
        res[lowerCaseKey] = headers[lowerCaseKey];
      }
    });
    if (Utils.isServerSide()) {
      const cookies = headers.cookie || headers.Cookie || '';
      cookies.split(';').forEach((cookie) => {
        const [key, value] = cookie.split('=');
        const lowerCaseKey = key.toLowerCase().trim();
        if (whiteListItems.includes(lowerCaseKey)) {
          res[lowerCaseKey] = value.trim();
        }
      });
    } else {
      whiteListItems.forEach((item) => {
        const value = Utils.getCookie(item) || '';
        if (value) {
          res[item] = value;
        }
      });
    }

    return res;
  }

  static isSuccessResponse(response) {
    return response.status && ((response.status >= 200 && response.status <= 300) || response.status === 303);
  }

  static addRecaptchaTokenHeader(config) {
    if (!Utils.isServerSide(config) && window.gRToken) {
      config.headers['x-g-recaptcha-token'] = window.gRToken;
    }
  }

  static addTraceIdHeader(config) {
    const traceIdHeader = X_TRACE_ID_HEADER;
    const traceIdHeaderLowerCase = traceIdHeader.toLowerCase();
    const traceId = Utils.isServerSide(config) ? '' : window[traceIdHeader] || window[traceIdHeaderLowerCase];
    config.headers[traceIdHeader] =
      config.headers[traceIdHeader] ||
      config.headers[traceIdHeaderLowerCase] ||
      traceId ||
      uuidv4();
  }

  static addRandomQueryParamToBypassCache(config) {
    const tries = config?.metadata?.tries || 0;
    const addRandomQueryParam = (config) => {
      config.params = config.params || {};
      config.params._tx = Utils.btoa(RequestResponseHelpers.generateRandomKey().slice(0, 8));
    };
    if (!['get', 'head'].includes(config.method.toLowerCase()) || (config.extraInfo && config.extraInfo.noCache)) {
      addRandomQueryParam(config);
    } else if (tries > 1) {
      // if not first try
      addRandomQueryParam(config);
    }
  }

  static generateRandomKey() {
    return `${uuidv4()}${uuidv4()}`.slice(Math.ceil(Math.random() * 20));
  }

  static addIdempotentHeader(config) {
    config.headers[X_IDEMPOTENT_HEADER] = config.headers[X_IDEMPOTENT_HEADER] || RequestResponseHelpers.generateRandomKey();
  }

  static getDefaultAxiosRetryConfig() {
    return {
      retries: 10,
      retryDelay: (retryCount) => retryCount * 5000,
      shouldResetTimeout: true,
      retryCondition: (error) => {
        console.error(`[Axios Retry Error]`, error);
        if (error && error.response && error.response.status) {
          const status = error.response.status;
          // 425 -> req is still ongoing
          return status === 425 || status > 500;
        }
        return true;
      }
    };
  };

  static get5xxStatuses() {
    return [500, 501, 502, 503, 504, 505, 506, 507];
  }

  static get4xxStatuses() {
    const statuses = [];
    for (let i = 0; i <= 17; i++) {
      statuses.push(400 + i);
    }
    return statuses;
  }

  static get3xxStatuses() {
    const statuses = [];
    for (let i = 0; i <= 17; i++) {
      statuses.push(300 + i);
    }
    return statuses;
  }

  static anyErrorStatus() {
    return [
      ...RequestResponseHelpers.get3xxStatuses(),
      ...RequestResponseHelpers.get4xxStatuses(),
      ...RequestResponseHelpers.get5xxStatuses()];
  }

  static getAxiosRetryConfig(retries, retryDelay, statusForRetries) {
    return {
      retries,
      retryDelay: (retryCount) => retryCount * retryDelay,
      shouldResetTimeout: true,
      retryCondition: (error) => {
        console.error(`[Axios Retry Error]`, error);
        if (error && error.response && error.response.status) {
          const status = error.response.status;
          return statusForRetries.includes(status);
        }
        return true;
      }
    };
  };
}

export default RequestResponseHelpers;
